// ─── Layout constants ─────────────────────────────────────────────────────────
// MAX_TRACKS: hard cap on how many items can stack in parallel within a lane.
// TEMP: raised from 3 to 50 to unbunch items and make them easier to move.
// Intended to return to 3 later. Lanes only grow as tall as actually used, so a
// high cap costs nothing visually until items actually stack that deep.
export const MAX_TRACKS = 50;
export const TRACK_H    = 76;
export const TRACK_PAD  = 1;
export const LANE_PAD   = 2;
export const CELL_W     = 130;

// ─── Sprint builder ───────────────────────────────────────────────────────────
export const SPRINT_COUNT = 38; // Feb 2026 through the sprint beginning Jul 26, 2027

export function buildSprints(year, count = SPRINT_COUNT) {
  const list = [];
  let start = new Date(year, 1, 23); // Feb 23 of given year
  for (let i = 0; i < count; i++) {
    const end = new Date(start);
    end.setDate(end.getDate() + 13);
    list.push({ id: i, start: new Date(start), end: new Date(end), label: `S${i + 1}` });
    start.setDate(start.getDate() + 14);
  }
  return list;
}
export let SPRINTS = buildSprints(2026);

// ─── Lanes ────────────────────────────────────────────────────────────────────
export const LANES = [
  { id: "pay",     label: "Team 1", capacity: 2, color: "#0C8C6E", bg: "#EEF9F5", light: "#C0F0DC", trackEven: "#F4FCF8", trackOdd: "#ECFAF3", trackOver: "#A8E8CE" },
  { id: "scrummy", label: "Team 2", capacity: 2, color: "#1A6BC4", bg: "#EEF4FC", light: "#B8D4F8", trackEven: "#F2F6FD", trackOdd: "#EAF0FB", trackOver: "#A8C8F4" },
  { id: "biz",     label: "Team 3", capacity: 1, color: "#C21066", bg: "#FBF0F6", light: "#F5C8E2", trackEven: "#FDF5F9", trackOdd: "#FAEEF5", trackOver: "#EFA8D0" },
];

// ─── Lane color palette ───────────────────────────────────────────────────────
export const COLOR_PALETTE = [
  { id:"green",  color:"#0C8C6E", bg:"#EEF9F5", light:"#C0F0DC", trackEven:"#F4FCF8", trackOdd:"#ECFAF3", trackOver:"#A8E8CE" },
  { id:"blue",   color:"#1A6BC4", bg:"#EEF4FC", light:"#B8D4F8", trackEven:"#F2F6FD", trackOdd:"#EAF0FB", trackOver:"#A8C8F4" },
  { id:"pink",   color:"#C21066", bg:"#FBF0F6", light:"#F5C8E2", trackEven:"#FDF5F9", trackOdd:"#FAEEF5", trackOver:"#EFA8D0" },
  { id:"purple", color:"#5A42E8", bg:"#F0EEFF", light:"#D4CAFC", trackEven:"#F4F2FF", trackOdd:"#EEEAFF", trackOver:"#C0B4F8" },
  { id:"orange", color:"#C45612", bg:"#FFF4EA", light:"#FECFA0", trackEven:"#FFF8F2", trackOdd:"#FFF0E4", trackOver:"#FCC080" },
  { id:"teal",   color:"#097898", bg:"#EAF8FD", light:"#B4E8F8", trackEven:"#EFF9FE", trackOdd:"#E5F6FD", trackOver:"#8CD8F0" },
  { id:"red",    color:"#BA1C1C", bg:"#FEF1F1", light:"#FAC8C8", trackEven:"#FEF5F5", trackOdd:"#FEEEEE", trackOver:"#F4A8A8" },
  { id:"indigo", color:"#3D30CF", bg:"#EDECFE", light:"#C8C4FC", trackEven:"#F1F0FF", trackOdd:"#EAEAFF", trackOver:"#B4B0F8" },
  { id:"amber",  color:"#B87010", bg:"#FFF7E0", light:"#FDEAB4", trackEven:"#FFFBEE", trackOdd:"#FFF5E0", trackOver:"#FAD880" },
  { id:"slate",  color:"#485468", bg:"#F4F6FA", light:"#D4DAE8", trackEven:"#F0F3F8", trackOdd:"#EBF0F6", trackOver:"#C0CAD8" },
];

// ─── Drivers ──────────────────────────────────────────────────────────────────
export const DRIVERS = {
  core:        { label: "Core / Consumer",      short: "Core",  color: "#5A42E8", bg: "#EEEAFF", border: "#C0B4F8" },
  revenue:     { label: "Revenue / Business",   short: "Rev",   color: "#966018", bg: "#FFF6E0", border: "#F0D490" },
  maintenance: { label: "Maintenance",          short: "Maint", color: "#0A7A6E", bg: "#EEFAF8", border: "#80DDD0" },
};

// ─── Business Areas ───────────────────────────────────────────────────────────
export const BUSINESS_AREAS = {
  pwgb:        { label: "PWGB+",                color: "#3D30CF", bg: "#EDECFE", border: "#B4B0F8" },
  advertising: { label: "Advertising",          color: "#C45612", bg: "#FFF4EA", border: "#FAC890" },
  subscription:{ label: "Subscription",         color: "#0A7840", bg: "#E4F8ED", border: "#80D8A8" },
  partners:    { label: "Partners / Rewards",   color: "#5A42E8", bg: "#F0EEFF", border: "#C4BCF8" },
  bizpages:    { label: "Business Pages",       color: "#1A6BC4", bg: "#EEF4FC", border: "#A8C8F4" },
  ux:          { label: "User Experience",      color: "#C21066", bg: "#FBF0F6", border: "#EFA8D0" },
  cs:          { label: "Customer Success",     color: "#0A7A6E", bg: "#EEFAF8", border: "#80DDD0" },
  mycar:       { label: "MyCar",                color: "#097898", bg: "#EAF8FD", border: "#68C4E0" },
  metrics:     { label: "Business Metrics",     color: "#B87010", bg: "#FFF7E0", border: "#F0D490" },
  legal:       { label: "Legal",                color: "#485468", bg: "#F4F6FA", border: "#C0CAD8" },
  banking:     { label: "Banking / Compliance", color: "#A81818", bg: "#FEF1F1", border: "#F0A0A0" },
};

// ─── Initiative Stages ────────────────────────────────────────────────────────
export const INITIATIVE_STAGES = {
  discovery:      { label: "Discovery",      icon: "🔍", color: "#7C3AED", bg: "#F5F3FF", border: "#C4B5FD" },
  committed:      { label: "Committed",      icon: "✅", color: "#0A7840", bg: "#E4F8ED", border: "#80D8A8" },
  experiment:     { label: "Experiment",     icon: "🧪", color: "#0369A1", bg: "#EFF6FF", border: "#7DD3FC" },
  infrastructure: { label: "Infrastructure", icon: "⚙️", color: "#485468", bg: "#F4F6FA", border: "#C0CAD8" },
};

// ─── Primary Surfaces ─────────────────────────────────────────────────────────
export const PRIMARY_SURFACES = {
  homepage:     { label: "Homepage",               color: "#C21066", bg: "#FBF0F6", border: "#EFA8D0" },
  offerwall:    { label: "Offer Wall",             color: "#5A42E8", bg: "#F0EEFF", border: "#C4BCF8" },
  rewards:      { label: "Rewards Page",           color: "#0A7840", bg: "#E4F8ED", border: "#80D8A8" },
  subscription: { label: "Subscription",           color: "#3D30CF", bg: "#EDECFE", border: "#B4B0F8" },
  onboarding:   { label: "Onboarding",             color: "#B87010", bg: "#FFF7E0", border: "#F0D490" },
  mycar:        { label: "MyCar Tab",              color: "#097898", bg: "#EAF8FD", border: "#68C4E0" },
  pushEmail:    { label: "Push / Email",           color: "#1A6BC4", bg: "#EEF4FC", border: "#A8C8F4" },
  gaming:       { label: "Gaming",                 color: "#C45612", bg: "#FFF4EA", border: "#FAC890" },
  payments:     { label: "Payments / Linked Acct", color: "#097898", bg: "#EAF8FD", border: "#68C4E0" },
  settings:     { label: "Settings",               color: "#485468", bg: "#F4F6FA", border: "#C0CAD8" },
  platform:     { label: "API / Platform",         color: "#A81818", bg: "#FEF1F1", border: "#F0A0A0" },
  findGas:      { label: "Find Gas",               color: "#2E7D32", bg: "#E8F5E9", border: "#81C784" },
};

// ─── Confidence Levels ────────────────────────────────────────────────────────
export const CONFIDENCE_LEVELS = {
  confirmed: { label: "Confirmed",   color: "#0A7840", bg: "#E4F8ED", border: "#80D8A8" },
  high:      { label: "High (80%+)", color: "#1A6BC4", bg: "#EEF4FC", border: "#A8C8F4" },
  medium:    { label: "Medium",      color: "#B87010", bg: "#FFF7E0", border: "#F0D490" },
  low:       { label: "Low (<50%)",  color: "#BA1C1C", bg: "#FEF1F1", border: "#FAC8C8" },
};

// ─── Statuses ─────────────────────────────────────────────────────────────────
export const STATUSES = {
  scheduled:    { label: "Scheduled",     color: "#0A6E5A", bg: "#E4F8F2", border: "#60D8B8" },
  inprogress:   { label: "In Progress",   color: "#5A42E8", bg: "#EEEAFF", border: "#C0B4F8" },
  notcommitted: { label: "Not Committed", color: "#8A5E10", bg: "#FFF4DC", border: "#E8C870" },
  backlog:      { label: "Backlog",       color: "#505C70", bg: "#EEF1F6", border: "#C0CAD8" },
  completed:    { label: "Completed",     color: "#0A6680", bg: "#DEEFF8", border: "#68C4E0" },
};

// ─── Release calendar constants ───────────────────────────────────────────────
export const EVENT_TYPES = {
  sprint_end:       { label: "Sprint End",               icon: "⬡", color: "#505C70", bg: "#EEF1F6" },
  dev_complete:     { label: "Dev Complete / Tag Merge",  icon: "●", color: "#BA1C1C", bg: "#FEF0F0" },
  app_store:        { label: "Submitted to App Store",    icon: "●", color: "#0A7840", bg: "#E4F8ED" },
  planned_release:  { label: "Planned Release Date",      icon: "★", color: "#B87010", bg: "#FFF4D8" },
  cloudops:         { label: "CloudOps Deployment",       icon: "☁", color: "#1A6BC4", bg: "#EEF4FC" },
  indian_holiday:   { label: "Indian Holiday",            icon: "◎", color: "#8A5E10", bg: "#FFF4DC" },
  us_ca_holiday:    { label: "US / Canadian Holiday",     icon: "◎", color: "#586070", bg: "#EEF1F6" },
  hot_patch:        { label: "Hot Patch Needed",          icon: "⚠", color: "#5A42E8", bg: "#EEEAFF" },
  regression:       { label: "Regression Testing",        icon: "⟳", color: "#097898", bg: "#E4F4FC" },
  marketing:        { label: "Marketing",                 icon: "◆", color: "#C21066", bg: "#FBF0F6" },
};
export const EVENT_PRIORITY = ['planned_release','dev_complete','app_store','cloudops','hot_patch','regression','marketing','sprint_end','indian_holiday','us_ca_holiday'];

// ─── Helper functions ─────────────────────────────────────────────────────────
export const getHalf       = (idx, year) => {
  const s = SPRINTS[idx];
  const y = s?.start.getFullYear() || year || 2026;
  return (s ? s.start.getMonth() >= 6 : idx >= 10) ? `H2 ${y}` : `H1 ${y}`;
};
export const getMonthLabel = (s)   => s.start.toLocaleString("default", { month: "short" });
export const formatDate    = (d)   => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
export const SCORE_STYLE   = (s)   => {
  if (s >= 4.0) return { bg: "#FFF4D8", text: "#8A5800", border: "#E8C870" };
  if (s >= 2.5) return { bg: "#E4F8F2", text: "#0A5040", border: "#60D8B8" };
  return                { bg: "#EEF1F6", text: "#485468", border: "#C0CAD8" };
};
export const RICE_STYLE    = (s)   => {
  if (s >= 70)  return { bg: "#FFF4D8", text: "#8A5800", border: "#E8C870" };
  if (s >= 40)  return { bg: "#E4F8F2", text: "#0A5040", border: "#60D8B8" };
  return                { bg: "#EEF1F6", text: "#485468", border: "#C0CAD8" };
};
export const scoreStyle    = (s, method) => method === "rice" ? RICE_STYLE(s) : SCORE_STYLE(s);
export const scoreLabel    = (s, method) => method === "rice" ? `${s || "—"}` : `★ ${s || "—"}`;
export const isHighScore   = (s, method) => method === "rice" ? s >= 70 : s >= 4.0;
export const VALUE_BADGE = (val) => {
  const n = parseInt((val || "").replace(/\D/g, "")) || 0;
  return n >= 500 ? "🔥" : n >= 200 ? "⭐" : "";
};

// Release calendar helpers
export function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
export function getFirstDayOfWeek(year, month) { return new Date(year, month, 1).getDay(); }
export function dateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
}

// ─── Track assignment ─────────────────────────────────────────────────────────
export function assignTracks(projects) {
  const sorted = [...projects]
    .filter((p) => p.sprint !== undefined)
    .sort((a, b) => a.sprint - b.sprint);
  const trackEnds = {};
  const result    = {};
  for (const p of sorted) {
    const key = p.lane;
    if (!trackEnds[key]) trackEnds[key] = new Array(MAX_TRACKS).fill(0);
    const ends = trackEnds[key];
    let assigned = -1;
    for (let t = 0; t < MAX_TRACKS; t++) {
      if (ends[t] <= p.sprint) { assigned = t; break; }
    }
    if (assigned === -1) assigned = 0;
    ends[assigned] = p.sprint + p.sprints;
    result[p.id] = assigned;
  }
  return result;
}

export function tracksUsed(projects, laneId, trackMap) {
  const ps = projects.filter((p) => p.lane === laneId && p.sprint !== undefined);
  if (ps.length === 0) return 1;
  return Math.max(...ps.map((p) => (trackMap[p.id] ?? 0))) + 1;
}

export function laneHeight(tracks) { return LANE_PAD * 2 + tracks * TRACK_H; }

export function getTracksUsedInSprint(projects, laneId, sprintIdx, trackMap) {
  const overlapping = projects.filter(
    (p) => p.lane === laneId &&
           p.sprint !== undefined &&
           p.sprint <= sprintIdx &&
           p.sprint + p.sprints > sprintIdx
  );
  if (overlapping.length === 0) return 0;
  const occupied = new Set(overlapping.map((p) => trackMap[p.id] ?? 0));
  return occupied.size;
}

// ─── Empty defaults ───────────────────────────────────────────────────────────
// No seed/demo content — a fresh roadmap (or a roadmap whose data hasn't loaded
// yet) starts empty rather than showing placeholder projects.
export const EMPTY_SCHEDULE = { events: [], codeFreezes: [], notes: [] };

// ─── Shared styles (colors reference CSS variables set by the active theme) ───
export const S = {
  root:        { fontFamily:"'Inter',system-ui,-apple-system,sans-serif", background:"var(--bg-root)", height:"100vh", color:"var(--text-primary)", display:"flex", flexDirection:"column", overflow:"hidden" },
  header:      { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 24px", borderBottom:"1px solid var(--border)", background:"var(--bg-header)", boxShadow:"0 1px 4px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)", flexShrink:0, position:"relative" },
  title:       { fontSize:16, fontWeight:900, letterSpacing:"-0.04em", color:"var(--text-primary)" },
  subtitle:    { fontSize:10, color:"var(--text-muted)", marginTop:1, fontWeight:600, letterSpacing:"0.02em" },
  headerRight: { display:"flex", gap:7, alignItems:"center" },
  btnPrimary:  { background:"var(--btn-primary)", color:"var(--btn-primary-txt)", border:"none", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", letterSpacing:"-0.01em", boxShadow:"0 2px 4px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)" },
  btnGhost:    { background:"var(--btn-ghost-bg)", color:"var(--btn-ghost-txt)", border:"1px solid var(--btn-ghost-border)", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", letterSpacing:"-0.01em" },
  btnDanger:       { background:"var(--color-danger-bg,#FEF2F2)", color:"var(--color-danger,#EF4444)", border:"1px solid var(--color-danger-border,#FCA5A5)", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", letterSpacing:"-0.01em" },
  btnClearFilter:  { fontSize:11, fontWeight:700, color:"var(--accent)", background:"transparent", border:"1px solid var(--accent)", borderRadius:5, padding:"3px 10px", cursor:"pointer", fontFamily:"inherit" },
  timelineRow: { display:"flex", alignItems:"stretch", borderBottom:"1px solid var(--border)", flexShrink:0 },
  halfHeader:  { fontSize:10, fontWeight:800, color:"#fff", letterSpacing:"0.1em", textTransform:"uppercase", padding:"5px 8px", borderRight:"1px solid rgba(255,255,255,0.2)", flexShrink:0 },
  monthHeader: { fontSize:11, fontWeight:600, color:"var(--text-secondary)", padding:"5px 8px", borderRight:"1px solid var(--border)", flexShrink:0 },
  sprintHeader:{ display:"flex", flexDirection:"column", alignItems:"center", padding:"5px 0", flexShrink:0, gap:1 },
  laneLabel:   { width:140, flexShrink:0, display:"flex", alignItems:"center", padding:"0 14px", borderRight:"1px solid var(--border)", background:"var(--bg-subtle)" },
  pill:        { fontSize:10, fontWeight:700, padding:"3px 7px", borderRadius:4, letterSpacing:"0.03em", lineHeight:1.5, whiteSpace:"nowrap" },
  pillMuted:   { fontSize:10, fontWeight:600, padding:"3px 7px", borderRadius:4, background:"var(--bg-hover)", color:"var(--text-secondary)", border:"1px solid var(--border)", lineHeight:1.5 },
  capRow:      { display:"flex", alignItems:"center", height:30, background:"var(--bg-subtle)", borderTop:"1px solid var(--border)", borderBottom:"1px solid var(--border)", flexShrink:0 },
  capCell:     { display:"flex", alignItems:"center", justifyContent:"center", height:"100%", flexShrink:0 },
  legend:      { display:"flex", gap:12, padding:"8px 24px", fontSize:10, color:"var(--text-secondary)", borderTop:"1px solid var(--border)", flexWrap:"wrap", alignItems:"center", background:"var(--bg-card)", flexShrink:0 },
  legendSect:  { fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:9 },
  legendItem:  { display:"flex", alignItems:"center", gap:4 },
  dot:         { width:8, height:8, borderRadius:"50%", display:"inline-block", flexShrink:0 },
  backlogPanel:{ width:216, flexShrink:0, background:"var(--bg-card)", borderLeft:"1px solid var(--border)", padding:14, overflowY:"auto" },
  backlogTitle:{ fontSize:10, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--text-muted)", marginBottom:8 },
  backlogCard: { background:"var(--bg-subtle)", border:"1px solid var(--border)", borderRadius:7, padding:"9px 11px", marginBottom:8, cursor:"grab", transition:"box-shadow 0.15s, transform 0.1s" },
  filterSelect:{ background:"var(--bg-subtle)", border:"1px solid var(--border)", borderRadius:6, padding:"5px 9px", fontSize:11, fontFamily:"inherit", color:"var(--text-primary)", cursor:"pointer" },
  iconBtn:     { background:"none", border:"none", cursor:"pointer", fontSize:14, padding:"3px 5px", borderRadius:5, fontFamily:"inherit" },
  overlay:     { position:"fixed", inset:0, background:"var(--overlay)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100, backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)", animation:"overlay-fade 0.15s ease both" },
  modal:       { background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:16, padding:26, width:440, boxShadow:"0 40px 96px rgba(0,0,0,0.22), 0 12px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)", maxHeight:"92vh", overflowY:"auto", animation:"modal-pop 0.22s cubic-bezier(0.34,1.56,0.64,1) both" },
  modalTitle:  { fontSize:15, fontWeight:800, marginBottom:18, color:"var(--text-primary)", letterSpacing:"-0.025em" },
  label:       { display:"block", fontSize:10, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700, marginBottom:5 },
  input:       { background:"var(--bg-input)", border:"1px solid var(--border)", borderRadius:8, padding:"8px 11px", color:"var(--text-primary)", fontSize:12, fontFamily:"inherit", width:"100%", boxSizing:"border-box", transition:"border-color 0.15s, box-shadow 0.15s" },
};
